import { useState } from 'react';
import {
  Container,
  Group,
  Title,
  Button,
  Table,
  Badge,
  Modal,
  Stack,
  TextInput,
  Textarea,
  Text,
  Skeleton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconEye } from '@tabler/icons-react';
import { useRFQList, useRFQDetail, useCreateRFQ } from '@/hooks/usePortalApi';

const STATUS_COLORS: Record<string, string> = {
  draft: 'gray',
  submitted: 'blue',
  quoted: 'cyan',
  awarded: 'green',
  closed: 'orange',
  cancelled: 'red',
};

export default function RFQPage() {
  const [createOpened, createHandlers] = useDisclosure(false);
  const [detailOpened, detailHandlers] = useDisclosure(false);
  const [selectedRFQId, setSelectedRFQId] = useState<number | null>(null);
  const [rfqTitle, setRFQTitle] = useState('');
  const [description, setDescription] = useState('');

  const { data: rfqs, isLoading } = useRFQList();
  const { data: rfqDetail } = useRFQDetail(selectedRFQId);
  const createRFQ = useCreateRFQ();

  const handleViewDetail = (id: number) => {
    setSelectedRFQId(id);
    detailHandlers.open();
  };

  const handleCreateRFQ = async () => {
    if (!rfqTitle.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please enter a title for the RFQ',
        color: 'red',
      });
      return;
    }

    try {
      await createRFQ.mutateAsync({
        title: rfqTitle,
        description,
      });
      notifications.show({
        title: 'Success',
        message: 'RFQ created successfully',
        color: 'green',
      });
      createHandlers.close();
      setRFQTitle('');
      setDescription('');
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to create RFQ',
        color: 'red',
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>Request for Quote</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={createHandlers.open}>
          New RFQ
        </Button>
      </Group>

      {isLoading ? (
        <Stack gap="xs">
          <Skeleton height={50} />
          <Skeleton height={50} />
          <Skeleton height={50} />
        </Stack>
      ) : !rfqs || rfqs.length === 0 ? (
        <Text ta="center" c="dimmed" py="xl">
          No RFQs found
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>RFQ #</Table.Th>
              <Table.Th>Title</Table.Th>
              <Table.Th>Date</Table.Th>
              <Table.Th>Items</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rfqs.map((rfq: any) => (
              <Table.Tr key={rfq.id}>
                <Table.Td>RFQ-{rfq.id}</Table.Td>
                <Table.Td>{rfq.title}</Table.Td>
                <Table.Td>{new Date(rfq.created_at).toLocaleDateString()}</Table.Td>
                <Table.Td>{rfq.items_count || 0}</Table.Td>
                <Table.Td>
                  <Badge color={STATUS_COLORS[rfq.status]} variant="light">
                    {rfq.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconEye size={14} />}
                    onClick={() => handleViewDetail(rfq.id)}
                  >
                    View
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal opened={createOpened} onClose={createHandlers.close} title="New RFQ" size="md">
        <Stack>
          <TextInput
            label="Title"
            placeholder="Enter RFQ title..."
            value={rfqTitle}
            onChange={(e) => setRFQTitle(e.currentTarget.value)}
            required
          />
          <Textarea
            label="Description / Requirements"
            placeholder="Describe your requirements in detail..."
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            minRows={4}
          />
          <Button onClick={handleCreateRFQ} loading={createRFQ.isPending}>
            Submit RFQ
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={detailOpened}
        onClose={detailHandlers.close}
        title={`RFQ-${selectedRFQId} Details`}
        size="lg"
      >
        {rfqDetail ? (
          <Stack>
            <div>
              <Text fw={600}>Title:</Text>
              <Text>{rfqDetail.title}</Text>
            </div>
            <Group>
              <Text fw={600}>Status:</Text>
              <Badge color={STATUS_COLORS[rfqDetail.status]} variant="light">
                {rfqDetail.status}
              </Badge>
            </Group>
            <Group>
              <Text fw={600}>Date Created:</Text>
              <Text>{new Date(rfqDetail.created_at).toLocaleString()}</Text>
            </Group>
            {rfqDetail.description && (
              <div>
                <Text fw={600}>Description:</Text>
                <Text>{rfqDetail.description}</Text>
              </div>
            )}

            {rfqDetail.items && rfqDetail.items.length > 0 && (
              <div>
                <Text fw={600} mb="xs">
                  Items:
                </Text>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Product</Table.Th>
                      <Table.Th>Quantity</Table.Th>
                      <Table.Th>Specifications</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rfqDetail.items.map((item: any) => (
                      <Table.Tr key={item.id}>
                        <Table.Td>{item.product_name || item.description}</Table.Td>
                        <Table.Td>{item.quantity}</Table.Td>
                        <Table.Td>{item.specifications}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </div>
            )}

            {rfqDetail.vendor_quotes && rfqDetail.vendor_quotes.length > 0 && (
              <div>
                <Text fw={600} mb="xs">
                  Vendor Quotes:
                </Text>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Vendor</Table.Th>
                      <Table.Th>Quote Amount</Table.Th>
                      <Table.Th>Lead Time</Table.Th>
                      <Table.Th>Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rfqDetail.vendor_quotes.map((quote: any) => (
                      <Table.Tr key={quote.id}>
                        <Table.Td>{quote.vendor_name}</Table.Td>
                        <Table.Td>{formatCurrency(quote.total_amount)}</Table.Td>
                        <Table.Td>{quote.lead_time_days} days</Table.Td>
                        <Table.Td>
                          <Badge
                            color={quote.is_awarded ? 'green' : 'gray'}
                            variant="light"
                          >
                            {quote.is_awarded ? 'Awarded' : 'Pending'}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </div>
            )}

            {rfqDetail.awarded_vendor && (
              <div>
                <Text fw={600}>Awarded Vendor:</Text>
                <Badge color="green" variant="light" size="lg">
                  {rfqDetail.awarded_vendor}
                </Badge>
              </div>
            )}
          </Stack>
        ) : (
          <Skeleton height={300} />
        )}
      </Modal>
    </Container>
  );
}
